import { z } from 'zod'
import { timestampSchema, uuidSchema } from './common.js'

export const languageVariantSchema = z.enum(['de-CH', 'de-DE', 'en-GB', 'en-US'])
export type LanguageVariant = z.infer<typeof languageVariantSchema>

// Anrede-Form: bei DE relevant (du/Sie), bei EN meist neutral.
// 'both' = Marke wechselt kontextabhaengig (z.B. LinkedIn=Sie, X=du).
export const formalitySchema = z.enum(['du', 'sie', 'both', 'en-neutral'])
export type Formality = z.infer<typeof formalitySchema>

// Em-Dash-Nutzung in der Brand-Voice. Diagnose-Feld zur Output-Steuerung:
// 'avoided'    -> Em-Dash gehoert in do_nots (Output-Skills vermeiden)
// 'occasional' -> bei de-CH ebenfalls in do_nots (Schweizer-Konvention)
// 'frequent'   -> Em-Dash bleibt erlaubt
export const emDashUsageSchema = z.enum(['frequent', 'occasional', 'avoided'])
export type EmDashUsage = z.infer<typeof emDashUsageSchema>

export const brandVoiceSourceTypeSchema = z.enum([
  'linkedin_pdf',
  'own_text',
  'blog_post',
  'newsletter',
  'book_excerpt',
  'transcript',
  'website_domain',
])
export type BrandVoiceSourceType = z.infer<typeof brandVoiceSourceTypeSchema>

export const brandTypeSchema = z.enum(['person', 'company', 'persona'])
export type BrandType = z.infer<typeof brandTypeSchema>

export const brandIdentitySourceTypeSchema = z.enum([
  'logo',
  'color_swatch',
  'font_sample',
  'presentation',
  'website',
  'reference_doc',
])
export type BrandIdentitySourceType = z.infer<typeof brandIdentitySourceTypeSchema>

export const brandVoiceSourceStatusSchema = z.enum([
  'uploaded',
  'analyzing',
  'analyzed',
  'error',
])
export type BrandVoiceSourceStatus = z.infer<typeof brandVoiceSourceStatusSchema>

export const channelRulesSchema = z
  .object({
    linkedin: z.string().optional(),
    x: z.string().optional(),
    blog: z.string().optional(),
    email: z.string().optional(),
  })
  .partial()
export type ChannelRules = z.infer<typeof channelRulesSchema>

export const voiceAnalysisSchema = z
  .object({
    tone: z.string().optional(),
    do_nots: z.array(z.string()).default([]),
    structural_patterns: z.string().optional(),
    hook_examples: z.array(z.string()).default([]),
    channel_rules: channelRulesSchema.default({}),
    usps: z.array(z.string()).default([]),
    language_variant: languageVariantSchema.optional(),
    /**
     * Anrede-Form. Bei deutschsprachigen Marken Pflicht-Information fuer
     * Output-Skills. 'both' = Marke wechselt kontextabhaengig.
     */
    formality: formalitySchema.optional(),
    /**
     * True wenn Material Schweizer-Rechtschreibung verwendet (ss statt ß,
     * Helvetismen wie parkieren/Velo/Tram, Apostroph-Tausender). Auto-
     * Detection durch den Analyzer. Bei true werden Schweizer-Konventionen
     * als Bedrock-do_nots aufgenommen.
     */
    swiss_orthography: z.boolean().optional(),
    /**
     * Em-Dash-Nutzung in der Brand-Voice — Diagnose-Feld zur Output-Steuerung.
     */
    em_dash_usage: emDashUsageSchema.optional(),
    confidence: z.number().min(0).max(1).optional(),
    last_updated: timestampSchema.optional(),
    source_count: z.number().int().nonnegative().optional(),
    /**
     * Argumentations-Bogen der Person/Marke. Makro-Stil ueber den Mikro-Stil
     * hinaus: induktiv vs deduktiv, These->Antithese, Fakt->Konsequenz,
     * Story->Pointe, etc. Gefuellt vom Reasoning-Consolidator (Phase 1.7).
     */
    argumentation_patterns: z.string().optional(),
    /**
     * Anzahl Bedrock-Patterns nach First-Principles-Filter (Soll: 5-8).
     * Hoeher = nicht ruthless genug. Diagnostik fuer Voice-Qualitaet.
     */
    bedrock_count: z.number().int().nonnegative().optional(),
    /**
     * Transparenz: wieviele Patterns aus wievielen Quellen, davon in mehreren
     * Quellen belegt. Format: "12 Patterns aus 2 Quellen, davon 7 in 2+ belegt".
     */
    evidence_summary: z.string().optional(),
    /**
     * Set vom LLM-Consolidator wenn er an seine Grenzen kommt (z.B. zu wenig
     * Material). Frontend kann das als Warn-Hint zeigen.
     */
    consolidator_notes: z.string().optional(),
    /**
     * Gesetzt vom Frontend wenn der User die voice_analysis manuell editiert.
     * Bei Reanalyze: Backend ueberschreibt voice_analysis NICHT direkt, sondern
     * gibt den AI-Vorschlag separat zurueck — User entscheidet via UI-Diff.
     */
    last_edited_by_user_at: timestampSchema.optional(),
  })
  .partial()
export type VoiceAnalysis = z.infer<typeof voiceAnalysisSchema>

export const brandStyleguideSchema = z
  .object({
    summary: z.string().optional(),
    colors: z
      .array(
        z.object({
          name: z.string(),
          hex: z.string(),
          usage: z.string().optional(),
        }),
      )
      .default([]),
    typography: z
      .object({
        heading: z.string().optional(),
        body: z.string().optional(),
        notes: z.string().optional(),
      })
      .partial()
      .default({}),
    visual_style: z.string().optional(),
    voice: z.string().optional(),
    logo_usage: z.string().optional(),
    gradient_style: z.string().optional(),
    presentation_style: z.string().optional(),
    imagery: z.string().optional(),
    layout_rules: z.array(z.string()).default([]),
    do_nots: z.array(z.string()).default([]),
    source_count: z.number().int().nonnegative().optional(),
    confidence: z.number().min(0).max(1).optional(),
    last_updated: timestampSchema.optional(),
    last_edited_by_user_at: timestampSchema.optional(),
  })
  .partial()
export type BrandStyleguide = z.infer<typeof brandStyleguideSchema>

export const brandStyleguideUpdateSchema = brandStyleguideSchema.partial()
export type BrandStyleguideUpdate = z.infer<typeof brandStyleguideUpdateSchema>

// Update-Payload fuer den Voice-Analysis-Editor (PATCH).
// Wir akzeptieren NUR die User-editierbaren Felder — Metadaten (last_updated,
// source_count, confidence) bleiben backend-managed.
export const voiceAnalysisUpdateSchema = z
  .object({
    tone: z.string().optional(),
    do_nots: z.array(z.string()).optional(),
    structural_patterns: z.string().optional(),
    hook_examples: z.array(z.string()).optional(),
    channel_rules: channelRulesSchema.optional(),
    usps: z.array(z.string()).optional(),
    language_variant: languageVariantSchema.optional(),
    formality: formalitySchema.optional(),
  })
  .partial()
export type VoiceAnalysisUpdate = z.infer<typeof voiceAnalysisUpdateSchema>

export const brandVoiceSourceSchema = z.object({
  id: uuidSchema,
  workspace_id: uuidSchema,
  brand_profile_id: uuidSchema,
  source_type: brandVoiceSourceTypeSchema,
  source_name: z.string().min(1).max(200),
  storage_path: z.string().nullable(),
  file_mime: z.string().nullable(),
  raw_text: z.string().nullable(),
  extracted_patterns: voiceAnalysisSchema,
  status: brandVoiceSourceStatusSchema,
  error_message: z.string().nullable(),
  uploaded_at: timestampSchema,
  analyzed_at: timestampSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
})
export type BrandVoiceSource = z.infer<typeof brandVoiceSourceSchema>

export const brandVoiceSourceCreateSchema = z.object({
  source_type: brandVoiceSourceTypeSchema,
  source_name: z.string().min(1).max(200).optional(),
})
export type BrandVoiceSourceCreate = z.infer<typeof brandVoiceSourceCreateSchema>

export const brandIdentitySourceSchema = z.object({
  id: uuidSchema,
  workspace_id: uuidSchema,
  brand_profile_id: uuidSchema,
  source_type: brandIdentitySourceTypeSchema,
  source_name: z.string().min(1).max(200),
  source_url: z.string().nullable(),
  storage_path: z.string().nullable(),
  file_mime: z.string().nullable(),
  raw_text: z.string().nullable(),
  extracted_styleguide: brandStyleguideSchema,
  status: brandVoiceSourceStatusSchema,
  error_message: z.string().nullable(),
  uploaded_at: timestampSchema,
  analyzed_at: timestampSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
})
export type BrandIdentitySource = z.infer<typeof brandIdentitySourceSchema>

export const brandVoiceSourceListResponseSchema = z.object({
  items: z.array(brandVoiceSourceSchema),
})
export type BrandVoiceSourceListResponse = z.infer<typeof brandVoiceSourceListResponseSchema>

export const consolidateVoiceResponseSchema = z.object({
  voice_analysis: voiceAnalysisSchema,
})
export type ConsolidateVoiceResponse = z.infer<typeof consolidateVoiceResponseSchema>
